import { ReactNode, useEffect, useState, lazy, Suspense } from 'react';
import CountryInfo from './pages/CountryInfo';
import { Search, Syringe, Shield, Plane, AlertCircle, Check, Calendar, DollarSign, MapPin, Info, ArrowLeft, Menu, X, ArrowRight } from 'lucide-react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// import { useParams, useNavigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { Country, GetCountries, GetCountryDiseases, Disease, UpdateCountriesLanguage } from './database.tsx';
import { getLocalizedPath } from './utils/urlLocalization';
import './i18n/i18n';
import Map from './components/Map';
import CookieConsentBanner from './components/CookieConsentBanner';
import TermsOfService from './pages/TermsOfService';
import ForClinics from './pages/ForClinics';
import SearchClinics from './pages/SearchClinics';
import i18n from './i18n/i18n';

// const Login = lazy(() => import('./pages/Login'));
// const Register = lazy(() => import('./pages/Register'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const SiteMap = lazy(() => import('./pages/SiteMap'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const Blog = lazy(() => import('./pages/Blog'));

const MapLoadingSpinner = () => (
    <div className="flex flex-col items-center justify-center h-full">
        <div className="animate-spin mb-2">
            <Syringe className="w-12 h-12 text-blue-600" />
        </div>
        <p className="text-gray-600 font-medium">Loading...</p>
    </div>
);

const PageLoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin">
            <Syringe className="w-12 h-12 text-blue-600" />
        </div>
    </div>
);

const diseaseDatabase: Record<string, Disease[]> = {};
let countries: Country[] | null = null;

function LanguageSwitcher() {
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
        UpdateCountriesLanguage();
        
        // Get the current path without the language prefix
        const pathWithoutLang = location.pathname.split('/').slice(2).join('/');
        const newPath = pathWithoutLang ? `/${lng}/${pathWithoutLang}` : `/${lng}`;
        navigate(newPath);
    };

    return (
        <div className="flex items-center space-x-2">
            <button
                onClick={() => changeLanguage('en')}
                className={`px-2 py-1 text-sm rounded ${i18n.language === 'en' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
                EN
            </button>
            <button
                onClick={() => changeLanguage('pl')}
                className={`px-2 py-1 text-sm rounded ${i18n.language === 'pl' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
                PL
            </button>
            <button
                onClick={() => changeLanguage('de')}
                className={`px-2 py-1 text-sm rounded ${i18n.language === 'de' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
                DE
            </button>
        </div>
    );
}

function Navigation() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const { t, i18n } = useTranslation();

    const getLocalizedLink = (path: string) => {
        return getLocalizedPath(path, i18n.language);
    };

    // Extract language from URL
    const currentLang = location.pathname.split('/')[1] || 'en';
    if (currentLang !== i18n.language) {
        i18n.changeLanguage(currentLang);
    }

    return (
        <nav className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-16">
                    <div className="flex items-center space-x-8">
                        <Link to={`/${i18n.language}`} className="flex items-center space-x-2" aria-label="SafeTripVax - Strona główna" title={t('nav.home')}>
                            <Plane className="w-6 h-6 text-blue-600" />
                            <span className="text-xl font-bold text-gray-800">SafeTripVax</span>
                        </Link>
                        <div className="hidden md:flex space-x-6">
                            <Link
                                to={`/${i18n.language}`}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === `/${i18n.language}` ? 'text-blue-600' : ''}`}
                                aria-label="Przejdź do strony głównej SafeTripVax"
                                title={t('nav.home')}
                            >
                                {t('nav.home')}
                            </Link>
                            <Link
                                to={getLocalizedLink('about')}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === getLocalizedLink('about') ? 'text-blue-600' : ''}`}
                                aria-label="Dowiedz się więcej o SafeTripVax"
                                title={t('nav.about')}
                            >
                                {t('nav.about')}
                            </Link>
                            <Link
                                to={getLocalizedLink('contact')}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === getLocalizedLink('contact') ? 'text-blue-600' : ''}`}
                                aria-label="Skontaktuj się z SafeTripVax"
                                title={t('nav.contact')}
                            >
                                {t('nav.contact')}
                            </Link>
                            <Link
                                to={getLocalizedLink('blog')}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === getLocalizedLink('blog') ? 'text-blue-600' : ''}`}
                                aria-label="Blog SafeTripVax"
                                title={t('nav.blog')}
                            >
                                {t('nav.blog')}
                            </Link>
                            <Link
                                to={getLocalizedLink('searchClinics')}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === getLocalizedLink('searchClinics') ? 'text-blue-600' : ''}`}
                                aria-label="Search Clinics"
                                title={t('nav.searchClinics')}
                            >
                                {t('nav.searchClinics')}
                            </Link>
                            <Link
                                to={getLocalizedLink('forClinics')}
                                className={`text-gray-600 hover:text-blue-600 transition-colors ${location.pathname === getLocalizedLink('forClinics') ? 'text-blue-600' : ''}`}
                                aria-label="Register Clinics"
                                title={t('nav.forClinics')}
                            >
                                {t('nav.forClinics')}
                            </Link>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center space-x-4">
                        <LanguageSwitcher />
                    </div>
                    <button
                        className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
                    >
                        {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

                {/* Mobile menu */}
                {isMenuOpen && (
                    <div className="md:hidden py-4 space-y-4">
                        <Link
                            to={`/${i18n.language}`}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Przejdź do strony głównej SafeTripVax - Menu mobilne"
                            title={t('nav.home')}
                        >
                            {t('nav.home')}
                        </Link>
                        <Link
                            to={getLocalizedLink('about')}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Dowiedz się więcej o SafeTripVax - Menu mobilne"
                            title={t('nav.about')}
                        >
                            {t('nav.about')}
                        </Link>
                        <Link
                            to={getLocalizedLink('contact')}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Skontaktuj się z SafeTripVax - Menu mobilne"
                            title={t('nav.contact')}
                        >
                            {t('nav.contact')}
                        </Link>
                        <Link
                            to={getLocalizedLink('blog')}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Blog SafeTripVax - Menu mobilne"
                            title={t('nav.blog')}
                        >
                            {t('nav.blog')}
                        </Link>
                        <Link
                            to={getLocalizedLink('searchClinics')}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Search Clinics - Mobile Menu"
                            title={t('nav.searchClinics')}
                        >
                            {t('nav.searchClinics')}
                        </Link>
                        <Link
                            to={getLocalizedLink('forClinics')}
                            className="block px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg"
                            onClick={() => setIsMenuOpen(false)}
                            aria-label="Blog SafeTripVax - Menu mobilne"
                            title={t('nav.forClinics')}
                        >
                            {t('nav.forClinics')}
                        </Link>
                        <div className="pt-4 border-t border-gray-100">
                            <div className="px-4 py-2">
                                <LanguageSwitcher />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </nav>
    );
}

function Footer() {
    const { t, i18n } = useTranslation();

    const getLocalizedLink = (path: string) => {
        return getLocalizedPath(path, i18n.language);
    };

    const getLinkText = (link: string) => {
        if (link === t('nav.cookies') && i18n.language === 'en') {
            return 'Cookies Policy';
        }
        if (link === t('nav.terms') && i18n.language === 'de') {
            return 'AGB';
        }
        return link;
    };

    const quickLinks = t('footer.quickLinks', { returnObjects: true }) as string[];

    const getLinkPath = (link: string) => {
        switch (link) {
            case t('nav.home'):
                return `/${i18n.language}`;
            case t('nav.about'):
                return getLocalizedLink('about');
            case 'Contact':
            case 'Kontakt':
                return getLocalizedLink('contact');
            case 'Sitemap':
            case 'Mapa witryny':
                return getLocalizedLink('sitemap');
            case t('nav.forClinics'):
                return getLocalizedLink('forClinics');
            case t('nav.blog'):
                return getLocalizedLink('blog');
            case t('nav.searchClinics'):
                return getLocalizedLink('searchClinics');
            case t('nav.terms'):
                return getLocalizedLink('terms');
            case t('nav.cookies'):
                return getLocalizedLink('cookies');
            case t('nav.privacy'):
                return getLocalizedLink('privacy');
            default:
                return `/${i18n.language}`;
        }
    };

    return (
        <footer className="bg-white mt-16">
            <div className="container mx-auto px-4 py-6">
                <div className="flex justify-center">
                    <div>
                        <ul className="flex flex-wrap justify-center gap-6">
                            {quickLinks.map((link, index) => (
                                <li key={index}>
                                    <Link
                                        to={getLinkPath(link)}
                                        className="text-gray-600 hover:text-blue-600 transition-colors"
                                        title={getLinkText(link)}
                                    >
                                        {getLinkText(link)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
                <div className="text-center mt-6 text-sm text-gray-500">
                    <p>© 2025 <a href="https://epko.tech" className="text-blue-600 hover:text-blue-800 transition-colors" title="EPKO sp. z o.o.">EPKO sp. z o.o.</a> {t('footer.rights')}</p>
                </div>
            </div>
        </footer>
    );
}

import { useParams, useNavigate } from 'react-router-dom';
function HomePage() {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const { t, i18n } = useTranslation();
    const [mapElement, setMapElement] = useState<ReactNode | null>(null);
    const [localizedCountries, setLocalizedCountries] = useState<Country[] | null>(null);
    const params = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load countries and set up map
    useEffect(() => {
        const loadCountries = async () => {
            setIsLoading(true);
            countries = await GetCountries();
            setLocalizedCountries(countries);
            setMapElement(
                <Suspense fallback={<MapLoadingSpinner />}>
                    <Map countries={countries ?? []} onCountrySelect={handleCountrySelect} />
                </Suspense>
            );
            setIsLoading(false);
        };
        loadCountries();
    }, []);

    // Update country names on language change
    useEffect(() => {
        if (countries) {
            countries = countries.map(country => ({
                ...country,
                name: i18n.t(`countries.${country.iso_code}`, { defaultValue: country.name })
            }));
            setLocalizedCountries([...countries]);
            setMapElement(
                <Suspense fallback={<MapLoadingSpinner />}>
                    <Map countries={countries} onCountrySelect={handleCountrySelect} />
                </Suspense>
            );
        }
    }, [i18n.language]);

    // Handle direct navigation to /:lang/:countryName
    useEffect(() => {
        if (!params || !params.countryName) return;
        const countryNameParam = decodeURIComponent(params.countryName);
        if (localizedCountries) {
            const found = localizedCountries.find(c => c.name.toLowerCase() === countryNameParam.toLowerCase());
            if (found) {
                // Only update if not already selected
                if (!selectedCountry || selectedCountry.iso_code !== found.iso_code) {
                    handleCountrySelect(found);
                }
            } else {
                if (selectedCountry !== null) setSelectedCountry(null);
            }
        }
    }, [params, localizedCountries]);

    // Helper to select country and navigate
    const handleCountrySelect = async (country: Country | null) => {
        if (country === null) {
            setSelectedCountry(null);
            setSearchTerm('');
            if (window.location.pathname !== `/${i18n.language}`) {
                navigate(`/${i18n.language}`);
            }
            return true;
        }
        const vd = diseaseDatabase[country?.iso_code];
        if (!vd) {
            diseaseDatabase[country.iso_code] = await GetCountryDiseases(country.iso_code, t);
        } else if (vd.length == 0) {
            return false;
        }
        setSelectedCountry(country);
        setSearchTerm('');
        const targetPath = `/${i18n.language}/${encodeURIComponent(country.name)}`;
        if (window.location.pathname !== targetPath) {
            navigate(targetPath);
        }
        return true;
    };

    // Update diseases on language change
    useEffect(() => {
        const refreshDiseaseData = async () => {
            if (selectedCountry) {
                diseaseDatabase[selectedCountry.iso_code] = await GetCountryDiseases(selectedCountry.iso_code, t);
                setSelectedCountry({ ...selectedCountry });
            }
        };
        refreshDiseaseData();
    }, [t]);

    const filteredCountries = (localizedCountries ?? []).filter(country =>
        country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <Helmet>
                <title>{t('home.title')} - SafeTripVax</title>
                <meta name="description" content={t('home.metaDescription', 'Find required vaccinations for your travel destination. Get detailed information about travel health requirements, vaccine recommendations, and safety tips for countries worldwide.')} />
            </Helmet>
            <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
                <div className="container mx-auto px-4 py-8">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Sidebar */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 sticky top-24">
                                <div className="space-y-4">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
                                        <input
                                            type="text"
                                            placeholder={t('home.searchPlaceholder')}
                                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm transition-all duration-300"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <h3 className="font-semibold text-gray-800 mb-2">
                                            {searchTerm ? t('home.searchResults') : t('home.popularDestinations')}
                                        </h3>
                                        {searchTerm ? (
                                            <div className="space-y-2">
                                                {filteredCountries.slice(0, 10).map((country) => (
                                                    <button
                                                        key={country.iso_code}
                                                        className="w-full p-3 text-left border rounded-lg hover:border-blue-300 hover:bg-blue-50 focus:border-blue-300 focus:outline-none transition-all hover:shadow-sm flex items-center"
                                                        onClick={() => handleCountrySelect(country)}
                                                    >
                                                        <MapPin className="w-4 h-4 text-blue-500 mr-2" />
                                                        <span className="font-medium text-gray-700">{country.name}</span>
                                                    </button>
                                                ))}
                                                {filteredCountries.length === 0 && (
                                                    <p className="text-gray-500 text-sm text-center py-2">
                                                        {t('home.noResults')}
                                                    </p>
                                                )}
                                                {filteredCountries.length > 10 && (
                                                    <p className="text-gray-500 text-sm text-center py-2">
                                                        {t('home.moreResults')}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            (localizedCountries ?? []).filter(country => (["PL", "HR", "GR", "ID", "TH", "MX", "EG", "TR", "MA", "BR"]).indexOf(country.iso_code) !== -1).sort((a: Country, b: Country) => a.name.localeCompare(b.name)).slice(0, isMobile ? 3 : 10).map((country) => (
                                                <button
                                                    key={country.iso_code}
                                                    className="w-full p-3 text-left border rounded-lg hover:border-blue-300 hover:bg-blue-50 focus:border-blue-300 focus:outline-none transition-all hover:shadow-sm flex items-center"
                                                    onClick={() => handleCountrySelect(country)}
                                                >
                                                    <Plane className="w-4 h-4 text-blue-500 mr-2" />
                                                    <span className="font-medium text-gray-700">{country.name}</span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="lg:col-span-3">
                            {!selectedCountry && (
                                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                                    <h1 className="text-2xl font-bold text-gray-800 mb-6">
                                        {t('home.title')}
                                    </h1>
                                    <div className="h-[500px] w-full">
                                        {isLoading ? (
                                            <div className="flex flex-col items-center justify-center h-full">
                                                <div className="animate-spin mb-2">
                                                    <Syringe className="w-12 h-12 text-blue-600" />
                                                </div>
                                                <p className="text-gray-600 font-medium">{t('home.loading')}</p>
                                            </div>
                                        ) : mapElement}
                                    </div>

                                    {/* Map Legend */}
                                    <div className="mt-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('home.mapLegend.title')}</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="flex items-center space-x-2">
                                                <div className="w-6 h-6 rounded bg-[#4ade80]"></div>
                                                <span className="text-gray-700">{t('home.mapLegend.safe')}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-6 h-6 rounded bg-[#FFD700]"></div>
                                                <span className="text-gray-700">{t('home.mapLegend.neutral')}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                <div className="w-6 h-6 rounded bg-[#f87171]"></div>
                                                <span className="text-gray-700">{t('home.mapLegend.unsafe')}</span>
                                            </div>
                                        </div>
                                        <div className="mt-4 text-sm text-gray-600">
                                            <p className="flex items-center">
                                                <Info className="w-4 h-4 mr-2 text-blue-600" />
                                                {t('home.mapLegend.clickInfo')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Travel Safety Tips */}
                                    <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                            <div className="flex items-center mb-4">
                                                <Calendar className="w-6 h-6 text-blue-600 mr-3" />
                                                <h3 className="text-lg font-semibold text-gray-800">{t('home.travelTips.planning.title')}</h3>
                                            </div>
                                            <p className="text-gray-600 text-sm">
                                                {t('home.travelTips.planning.description')}
                                            </p>
                                        </div>

                                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                            <div className="flex items-center mb-4">
                                                <Shield className="w-6 h-6 text-blue-600 mr-3" />
                                                <h3 className="text-lg font-semibold text-gray-800">{t('home.travelTips.safety.title')}</h3>
                                            </div>
                                            <p className="text-gray-600 text-sm">
                                                {t('home.travelTips.safety.description')}
                                            </p>
                                        </div>

                                        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                                            <div className="flex items-center mb-4">
                                                <DollarSign className="w-6 h-6 text-blue-600 mr-3" />
                                                <h3 className="text-lg font-semibold text-gray-800">{t('home.travelTips.costs.title')}</h3>
                                            </div>
                                            <p className="text-gray-600 text-sm">
                                                {t('home.travelTips.costs.description')}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-6 bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6">
                                        <div className="flex items-start">
                                            <AlertCircle className="w-6 h-6 text-blue-600 mr-4 mt-1 flex-shrink-0" />
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('home.importantInfo.title')}</h3>
                                                <ul className="space-y-2 text-sm text-gray-600">
                                                    {(t('home.importantInfo.tips', { returnObjects: true }) as string[]).map((tip: string, index: number) => (
                                                        <li key={index} className="flex items-start">
                                                            <Check className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                                                            <span>{tip}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Country Info */}
                            {selectedCountry && diseaseDatabase[selectedCountry.iso_code] && (
                                <CountryInfo
                                    country={selectedCountry}
                                    diseases={diseaseDatabase[selectedCountry.iso_code]}
                                    onBack={() => handleCountrySelect(null)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

function App() {
    const l = i18n.language;
    if (l.match("-")) {
        i18n.changeLanguage(l.split("-")[0]);
    }
    return (
        <HelmetProvider>
            <Router>
                <div className="min-h-screen flex flex-col">
                    <Navigation />
                    <main className="flex-grow">
                        <Suspense fallback={<PageLoadingSpinner />}>
                            <Routes>
                                <Route path="/" element={<Navigate to="/en" replace />} />
                                <Route path="/:lang" element={<HomePage />} />
                                <Route path="/:lang/:countryName" element={<HomePage />} />
                                <Route path="/:lang/about" element={<About />} />
                                <Route path="/:lang/o-nas" element={<About />} />
                                <Route path="/:lang/uber-uns" element={<About />} />
                                <Route path="/:lang/contact" element={<Contact />} />
                                <Route path="/:lang/kontakt" element={<Contact />} />
                                <Route path="/:lang/sitemap" element={<SiteMap />} />
                                <Route path="/:lang/mapa-witryny" element={<SiteMap />} />
                                <Route path="/:lang/cookies-policy" element={<CookiePolicy />} />
                                <Route path="/:lang/polityka-cookies" element={<CookiePolicy />} />
                                <Route path="/:lang/cookie-richtlinie" element={<CookiePolicy />} />
                                <Route path="/:lang/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/:lang/polityka-prywatnosci" element={<PrivacyPolicy />} />
                                <Route path="/:lang/datenschutz" element={<PrivacyPolicy />} />
                                <Route path="/:lang/terms" element={<TermsOfService />} />
                                <Route path="/:lang/regulamin" element={<TermsOfService />} />
                                <Route path="/:lang/agb" element={<TermsOfService />} />
                                <Route path="/:lang/blog" element={<Blog />} />
                                <Route path="/:lang/blog/:slug" element={<Blog />} />
                                <Route path="/:lang/search-clinics" element={<SearchClinics />} />
                                <Route path="/:lang/szukaj-przychodni" element={<SearchClinics />} />
                                <Route path="/:lang/kliniken-suchen" element={<SearchClinics />} />
                                <Route path="/:lang/dla-przychodni" element={<ForClinics />} />
                                <Route path="/:lang/fur-kliniken" element={<ForClinics />} />
                                <Route path="/:lang/for-clinics" element={<ForClinics />} />
                                <Route path="*" element={<Navigate to={(l=="pl"||l=="pl"||l=="pl") ? `/${l}` : `/en`} replace />} />
                            </Routes>
                        </Suspense>
                    </main>
                    <Footer />
                    <CookieConsentBanner />
                </div>
            </Router>
        </HelmetProvider>
    );
}

export default App;